'use client';

import '@/styles/libs/swiper-overrides.css';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Plus } from './Icons';

const TESTIMONIAL_INTERVAL_MS = 5000;

export interface HomeCustomerStoriesProps {
  hideComponent?: boolean;
  title: React.ReactNode;
  cta: {
    href: string;
    title: string;
  };
  gridItems: CustomerStoryGridItemProps[];
  testimonials: CustomerStoryTestimonialProps[];
}

interface CustomerStoryGridItemProps {
  backgroundImage?: string;
  backgroundColor?: string;
  logo: React.ReactNode;
  title: string;
  description: string;
  href: string;
  textColor?: string;
  buttonColor?: string;
}

const CustomerStoryGridItem: FC<CustomerStoryGridItemProps> = ({
  backgroundImage,
  backgroundColor = 'bg-white',
  logo,
  title,
  description,
  href,
  textColor = 'text-white',
  buttonColor = 'button-primary-white',
}) => {
  // Transition type use for all hover effects
  const transition = {
    duration: 0.5,
    type: 'spring',
    bounce: 0,
    ease: 'easeInOut',
  };
  // Motion specs for animated elements.
  const backgroundMotion = {
    rest: {
      scale: 1,
    },
    hover: {
      scale: 1.05,
    },
  };
  const textMotion = {
    rest: {
      translateY: 0,
    },
    hover: {
      translateY: -30,
    },
  };
  const buttonMotion = {
    rest: {
      translateY: 40,
    },
    hover: {
      translateY: 0,
    },
  };
  const plusMotion = {
    rest: {
      opacity: 1,
    },
    hover: {
      opacity: 0,
    },
  };

  return (
    // Lots of internal elements are animated on wrapper hover - using variants off wrapper's whileHover to sync animations
    <motion.a
      initial="rest"
      whileHover="hover"
      transition={transition}
      variants={{
        rest: {
          backgroundColor: '#FFFFFF00',
        },
        hover: {
          backgroundColor: '#D4B5F4',
        },
      }}
      href={href}
      className="p-1 col-span-1 block relative  rounded-[20px]"
    >
      <div
        className={`w-37 sm:min-w-38 md:min-w-min rounded-[12px] overflow-hidden relative h-full sm:w-full p-4 flex flex-col gap-4 ${textColor} ${backgroundColor} bg-cover bg-center`}
      >
        {/* Optional background image - includes a mild zoom effect on hover */}
        {backgroundImage && (
          <motion.div
            transition={transition}
            variants={backgroundImage ? backgroundMotion : undefined}
            className="absolute inset-0"
          >
            <Image src={backgroundImage} fill className="object-cover" alt="" />
          </motion.div>
        )}
        <div className={`relative z-10 flex flex-col h-full overflow-hidden `}>
          <div className="max-h-6 h-6">{logo}</div>
          {/* Text content slides up on hover */}
          <motion.div variants={textMotion} transition={transition} className="md:mb-3">
            <h3 className={`text-display1 font-500 mt-14`}>{title}</h3>
            <p className={`text-p2 `}>{description}</p>
          </motion.div>
          <div className="absolute bottom-0">
            {/* Button slides into view on hover - is  */}
            <motion.div variants={buttonMotion} transition={transition} className="mt-auto">
              <button className={cx(' block button button-size-medium', buttonColor)}>Read Customer Story</button>
            </motion.div>
          </div>
        </div>
        {/* Plus icon dissapears on hover */}
        {/* bg-current is used to match the text color of parent element */}
        <motion.div
          variants={plusMotion}
          transition={{
            duration: 0.2,
            type: 'linear',
          }}
          className={`flex bg-current absolute bottom-2 right-2 rounded-full w-[26px] h-[26px] items-center justify-center `}
        >
          <Plus strokeColor={textColor === 'text-white' ? 'stroke-blue-900' : 'stroke-white'} />
        </motion.div>
      </div>
    </motion.a>
  );
};

interface CustomerStoryTestimonialProps {
  logo: React.ReactNode;
  quote: string;
  author: string;
  title: string;
}

// Animated testimonial progress bubbles - used on desktop only ATM due to Swiper constraints
const TestimonialProgressBubbles = ({
  testimonials,
  currentTestimonialIndex,
  SetFixedTestimonial,
  isAnimating,
}: {
  testimonials: CustomerStoryTestimonialProps[];
  currentTestimonialIndex: number;
  SetFixedTestimonial: (index: number) => void;
  isAnimating: boolean;
}) => {
  return (
    <>
      {testimonials.map((_, index) => {
        const duration = isAnimating ? TESTIMONIAL_INTERVAL_MS / 1000 : 1;

        return isAnimating ? (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-gray-300 relative rounded-[9px] overflow-hidden"
            initial={{ width: '9px', height: '9px' }}
            animate={{ width: index === currentTestimonialIndex ? ['9px', '35px', '35px', '9px'] : '9px' }}
            transition={{ duration: duration, times: [0, 0.1, 0.9, 1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '9px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['9px', '35px', '9px'] : '9px',
              }}
              transition={{ duration: duration, times: [0, 0.9, 1] }}
              className="w-[9px] h-[9px] absolute z-1  rounded-[9px] bg-white cursor-pointer"
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        ) : (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-gray-300 relative rounded-[9px] overflow-hidden"
            initial={{ width: '9px', height: '9px' }}
            animate={{ width: index === currentTestimonialIndex ? ['9px', '35px'] : '9px' }}
            transition={{ duration: duration, times: [0, 0.1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '9px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['9px', '35px'] : '9px',
              }}
              transition={{ duration: duration, times: [0, 0.9] }}
              className="w-[9px] h-[9px] absolute z-1  rounded-[9px] bg-white cursor-pointer"
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        );
      })}
    </>
  );
};

const DesktopCustomerStoryTestimonial = ({ testimonials }: { testimonials: CustomerStoryTestimonialProps[] }) => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const SetFixedTestimonial = (index: number) => {
    setIsAnimating(false);
    setCurrentTestimonialIndex(index);
  };

  useEffect(() => {
    if (isAnimating) {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
      }, TESTIMONIAL_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [testimonials, currentTestimonialIndex, isAnimating]);

  return (
    <div className="relative h-full">
      <AnimatePresence>
        {/* Progress bubbles */}
        <div className="absolute z-10 top-0 right-0 -translate-x-1/2 flex gap-1 mb-4 bg-gray-200 rounded-20 p-1">
          <TestimonialProgressBubbles
            testimonials={testimonials}
            currentTestimonialIndex={currentTestimonialIndex}
            SetFixedTestimonial={SetFixedTestimonial}
            isAnimating={isAnimating}
          />
        </div>

        {testimonials.map((testimonial, index) => {
          return (
            <motion.div
              key={`customer-story-testimonial-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute  p-4 inset-0 w-full h-full  flex flex-col justify-between"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex max-h-4"
              >
                {testimonial.logo}
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="text-16/24 md:text-h4 font-serif font-500 text-blue-900"
              >
                {testimonial.quote}
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col"
              >
                <span className="text-14/20 mb-[4px] md:text-p2 font-400 text-blue-1000">{testimonial.author}</span>
                <span className="text-14/20 md:text-micro2 text-blue-1000 uppercase">{testimonial.title}</span>
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const MobileCustomerStoryTestimonial = ({ testimonials }: { testimonials: CustomerStoryTestimonialProps[] }) => {
  return (
    <div className="md:hidden p-1 col-span-2 col-start-2 row-start-2 bg-transparent relative  rounded-[12px]">
      {/* Custom pagination classes emulating desktop pagination from swiper-overrides.css */}
      <Swiper
        pagination={{
          clickable: true,
          type: 'bullets',
          el: '.swiper-pagination-white',
          bulletClass: 'swiper-pagination-bullet-white',
          bulletActiveClass: 'swiper-pagination-bullet-white-active',
        }}
        slidesPerView={1.1}
        centeredSlides={true}
        spaceBetween={20}
        autoplay={{
          delay: 5000,
          disableOnInteraction: true,
        }}
        loop={true}
        modules={[Autoplay, Pagination]}
      >
        {testimonials.map((testimonial) => (
          <SwiperSlide key={`customer-story-testimonial-${testimonial.author}`}>
            <div className="py-4 flex flex-col justify-center items-center text-center">
              <div className="max-h-10 mx-auto mb-3">{testimonial.logo}</div>
              <p className="mb-3 text-h4 font-serif font-500 text-blue-900">{testimonial.quote}</p>
              <div className="flex flex-col">
                <span className="text-p2 mb-[6px] font-400 text-blue-1000">{testimonial.author}</span>
                <span className="text-micro2 text-blue-1000 uppercase">{testimonial.title}</span>
              </div>
            </div>
          </SwiperSlide>
        ))}
        <div className="swiper-pagination-white" />
      </Swiper>
    </div>
  );
};

const HomeCustomerStories: FC<HomeCustomerStoriesProps> = ({
  hideComponent = false,
  title,
  gridItems = [],
  testimonials = [],
  cta,
}) => {
  if (hideComponent) return null;
  return (
    <div
      className={`${hideComponent ? 'hidden' : 'flex'
        } bg-white-100 mt-8 md:mt-22 px-container-mobile md:px-container-desktop py-[120px]`}
    >
      <div className="mx-auto max-w-grid w-full flex flex-col gap-6">
        <div className="flex flex-col justify-center items-center">
          <h3 className="text-center text-pretty text-30/35 md:text-display3 sm:max-w-68 md:max-w-85 font-500 tracking-tight">{title}</h3>
          <div className="mt-4 flex items-center hover-arrow-wrapper">
            <div className="flex font-500 text-blue-1000 text-p2 w-full">
              <Link
                className="font-500 text-blue-900 text-p2"
                href={cta.href}
              >   {cta.title}</Link>
            </div>
            <div className="flex ml-[8px] relative w-[16px] h-[16px] bg-[url(https://front.com/webassets/rebrand/black-right-arrows.png)] hover-arrow" />
          </div>

        </div>
        {/* Desktop Cards & Testimonial */}
        <div className="hidden md:grid md:grid-cols-3 md:grid-rows-3 xl:grid-cols-4 xl:grid-rows-2 md:gap-x-0 gap-y-0">
          {gridItems.map((item) => (
            <CustomerStoryGridItem key={item.href} {...item} />
          ))}
          <div className="block p-1 row-start-3 md:col-span-3 xl:row-start-2 xl:col-span-2 xl:col-start-2  bg-transparent relative  rounded-[12px]">
            <DesktopCustomerStoryTestimonial testimonials={testimonials} />
          </div>
        </div>
        {/* Mobile Cards */}
        <div className="md:hidden -mx-3 p-0 col-span-2 col-start-2 row-start-2 bg-transparent relative  rounded-[12px]">
          <Swiper slidesPerView={1.25} loop={true} centeredSlides={true} centeredSlidesBounds={true} spaceBetween={0}>
            {gridItems.map((item) => (
              <SwiperSlide key={item.href}>
                <CustomerStoryGridItem key={item.href} {...item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {/* Mobile Testimonial */}
        <MobileCustomerStoryTestimonial testimonials={testimonials} />
      </div>
    </div>
  );
};

export default HomeCustomerStories;
